import React, { useState, useEffect } from "react";
import { useSwipeable } from "react-swipeable";
import { Box, Button, Fade, IconButton, Stack } from "@mui/material";
import { GridView } from "@mui/icons-material";
import {
  FaArrowLeft,
  FaShareAlt,
  FaHeart as FaHeartSolid,
  FaRegHeart,
} from "react-icons/fa";

const KitchenImages = ({
  kitchen,
  onImageClick,
  isFavourite,
  onFavouriteToggle,
  onShareClick,
}) => {
  const defaultImage =
    "https://airkitchenz-assets.s3.us-west-1.amazonaws.com/assets/KitchenComingSoon.png";
  const imageArray =
    kitchen?.Images?.length > 0 ? [...kitchen.Images] : [defaultImage];

  const preparedImages = Array(5).fill(defaultImage);
  for (let i = 0; i < Math.min(imageArray.length, 5); i++) {
    preparedImages[i] = imageArray[i];
  }

  const isRealImage = (img) => img !== defaultImage;
  const hasEnoughRealImages = imageArray.length >= 5;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [animation, setAnimation] = useState("");

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setCurrentIndex((prev) => (prev + 1) % imageArray.length);
    },
    onSwipedRight: () => {
      setCurrentIndex((prev) =>
        prev === 0 ? imageArray.length - 1 : (prev - 1) % imageArray.length
      );
    },
    onTap: (e) => {
      e.event.stopPropagation();
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
    trackTouch: true,
    delta: 50,
  });

  const preparedImage = imageArray[currentIndex] || defaultImage;

  const handleFavouriteToggle = (e) => {
    e.stopPropagation();
    onFavouriteToggle(e);
    setAnimation(isFavourite ? "animate-shake" : "animate-pop");
    setTimeout(() => setAnimation(""), 300);
  };

  return (
    <Box>
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "relative",
          width: "100%",
          height: { xs: "300px" },
          overflow: "hidden",
          borderRadius: "0px 0px 12px 12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          m: 0,
          p: 0,
        }}
        {...handlers}
      >
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            right: 8,
            display: "flex",
            justifyContent: "space-between",
            zIndex: 1,
            p: 0,
          }}
        >
          <IconButton
            onClick={() => window.history.back()}
            sx={{
              color: "#666",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                color: "#333",
              },
              borderRadius: "50%",
              width: 40,
              height: 40,
              p: 1,
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
            }}
          >
            <FaArrowLeft />
          </IconButton>
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={onShareClick}
              sx={{
                color: "#666",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  color: "#1a73e8",
                },
                borderRadius: "50%",
                width: 40,
                height: 40,
                p: 1,
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
              }}
            >
              <FaShareAlt />
            </IconButton>
            <IconButton
              onClick={handleFavouriteToggle}
              sx={{
                color: isFavourite ? "#e63946" : "#666",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  color: isFavourite ? "#d32f2f" : "#1a73e8",
                },
                borderRadius: "50%",
                width: 40,
                height: 40,
                p: 1,
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
                "&.animate-pop": {
                  animation: "pop 0.3s ease-out",
                },
                "&.animate-shake": {
                  animation: "shake 0.3s ease-out",
                },
              }}
              className={animation}
            >
              {isFavourite ? <FaHeartSolid /> : <FaRegHeart />}
            </IconButton>
          </Stack>
        </Box>

        <img
          src={preparedImage}
          alt={`kitchen image ${currentIndex + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "0px 0px 12px 12px",
            cursor: "pointer",
            userSelect: "none",
            pointerEvents: "auto",
            display: "block",
            margin: 0,
            padding: 0,
          }}
          onClick={() => onImageClick(currentIndex)}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10px",
            right: "10px",
            color: "#fff",
            background: "rgba(0, 0, 0, 0.6)",
            padding: "4px 12px",
            borderRadius: "20px",
            fontSize: "14px",
            zIndex: 2,
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
          }}
        >
          {`${String(currentIndex + 1).padStart(2, "0")}/${String(
            imageArray.length
          ).padStart(2, "0")}`}
        </Box>
      </Box>

      <Fade in={fade} timeout={300}>
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "row",
            gap: 1,
            height: "400px",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{ flex: 1, overflow: "hidden", borderTopLeftRadius: "16px" }}
          >
            <img
              src={preparedImages[0]}
              alt="Main"
              onClick={() => isRealImage(preparedImages[0]) && onImageClick(0)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: isRealImage(preparedImages[0]) ? "pointer" : "default",
              }}
            />
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {preparedImages.slice(1, 4).map((img, idx) => (
              <Box
                key={idx + 1}
                sx={{
                  width: "calc(50% - 4px)",
                  height: "calc(50% - 4px)",
                  overflow: "hidden",
                }}
                onClick={() => isRealImage(img) && onImageClick(idx + 1)}
              >
                <img
                  src={img}
                  alt={`Sub ${idx + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    cursor: isRealImage(img) ? "pointer" : "default",
                  }}
                />
              </Box>
            ))}

            <Box
              sx={{
                width: "calc(50% - 4px)",
                height: "calc(50% - 4px)",
                position: "relative",
                cursor: "pointer",
                borderBottomRightRadius: "16px",
                overflow: "hidden",
                cursor: isRealImage(preparedImages[4]) ? "pointer" : "default",
              }}
              onClick={() => isRealImage(preparedImages[4]) && onImageClick(4)}
            >
              <img
                src={preparedImages[4]}
                alt="Show All"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              {hasEnoughRealImages && (
                <Button
                  variant="contained"
                  startIcon={<GridView />}
                  onClick={() => onImageClick(4)}
                  sx={{
                    position: "absolute",
                    bottom: 10,
                    right: 10,
                    borderRadius: "20px",
                    backgroundColor: "#fff",
                    color: "#333",
                    fontSize: "0.75rem",
                    textTransform: "none",
                    padding: "6px 12px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  Show All Photos
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

export default KitchenImages;
